import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Info,
  Phone,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { crmV2API } from '../services/api';
import { getOutboundCallUrl, getCurrentUserIdForCalls } from '../lib/apiConfig';
import { useUserPhoneNumbers } from '../hooks/useUserPhoneNumbers';

function clsx(...arr) {
  return arr.filter(Boolean).join(' ');
}

function formatDate(val) {
  if (!val) return '—';
  const d = typeof val === 'string' ? new Date(val) : val;
  return d.toLocaleString();
}

function truncate(str, n = 80) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n - 1) + '…' : str;
}

function useDebounced(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

/** Match field name (case-insensitive) against patterns. */
function fieldMatches(name, patterns) {
  const n = (name || '').toLowerCase();
  return patterns.some((p) => n.includes(p) || n === p);
}

/** Extract phone, firstName, lastName, dob from record.fields using group.fieldNames. */
function getContactForCall(fieldNames, fields) {
  if (!fieldNames || !fields) return { phone: '', firstName: '', lastName: '', dob: '' };
  const get = (patterns) => {
    const key = fieldNames.find((f) => fieldMatches(f, patterns));
    return key ? String(fields[key] ?? '').trim() : '';
  };
  const phone = get(['phone', 'mobile', 'cell', 'tel', 'telephone']);
  const firstName = get(['first name', 'firstname', 'first name']) || get(['name']);
  const lastName = get(['last name', 'lastname']);
  const dobRaw = get(['dob', 'date of birth', 'birth date', 'birthdate']);
  let dob = '';
  if (dobRaw) {
    const d = new Date(dobRaw);
    if (!Number.isNaN(d.getTime())) dob = d.toISOString().slice(0, 10);
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dobRaw)) dob = dobRaw;
  }
  return { phone, firstName, lastName, dob };
}

/** Normalize phone to digits for display; API will E164. */
function phoneDigits(value) {
  return (value || '').replace(/\D/g, '');
}

export default function GroupRecordsView({ group, onBack }) {
  const { allNumbers, loading: numbersLoading } = useUserPhoneNumbers();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [callRecord, setCallRecord] = useState(null);
  const [callFromNumber, setCallFromNumber] = useState('');
  const [callStarting, setCallStarting] = useState(false);

  const dq = useDebounced(q, 400);
  const groupId = group?.id || '';
  const contactForCall = callRecord ? getContactForCall(group?.fieldNames || [], callRecord.fields || {}) : null;
  const numbersWithOutbound = (allNumbers || []).filter((n) => n.outboundScriptId);
  const selectedNumberDoc = (allNumbers || []).find((n) => n.number === callFromNumber || n.displayNumber === callFromNumber);

  async function load() {
    if (!groupId) return;
    setLoading(true);
    setError('');
    try {
      const res = await crmV2API.listRecords(groupId, {
        page,
        limit,
        q: dq || undefined,
      });
      const data = res.data || {};
      setRecords(Array.isArray(data.records) ? data.records : []);
      setTotal(Number(data.total) || 0);
      setHasMore(Boolean(data.hasMore));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load records');
      setRecords([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, page, limit, dq]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSelected(null);
        setEditRecord(null);
        setDeleteRecord(null);
        setCallRecord(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (callRecord && !callFromNumber && numbersWithOutbound[0]) {
      setCallFromNumber(numbersWithOutbound[0].number);
    }
  }, [callRecord, callFromNumber, numbersWithOutbound]);

  async function handleSaveEdit() {
    if (!editRecord?.id) return;
    setEditSaving(true);
    try {
      await crmV2API.updateRecord(editRecord.id, { fields: editDraft });
      setRecords((prev) =>
        prev.map((r) => (r.id === editRecord.id ? { ...r, fields: editDraft, updatedAt: new Date().toISOString() } : r))
      );
      if (selected?.id === editRecord.id) setSelected((s) => (s ? { ...s, fields: editDraft } : s));
      setEditRecord(null);
      setEditDraft({});
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteRecord?.id) return;
    setDeleteDeleting(true);
    try {
      await crmV2API.deleteRecord(deleteRecord.id);
      setRecords((prev) => prev.filter((r) => r.id !== deleteRecord.id));
      setTotal((t) => Math.max(0, t - 1));
      if (selected?.id === deleteRecord.id) setSelected(null);
      setDeleteRecord(null);
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to delete');
    } finally {
      setDeleteDeleting(false);
    }
  }

  function openCallModal(record) {
    setCallRecord(record);
    const firstWithScript = numbersWithOutbound[0];
    setCallFromNumber(firstWithScript ? firstWithScript.number : '');
  }

  async function startCall() {
    if (!callRecord || !contactForCall?.phone) {
      alert('This record has no phone number.');
      return;
    }
    const fromNum = selectedNumberDoc?.number || callFromNumber;
    if (!fromNum) {
      alert('Select a "from" phone number.');
      return;
    }
    if (!selectedNumberDoc?.outboundScriptId) {
      alert('Selected number has no outbound script. Configure it in Phone Numbers.');
      return;
    }
    setCallStarting(true);
    try {
      const token = localStorage.getItem('token');
      const placedByUserId = getCurrentUserIdForCalls();
      const res = await fetch(getOutboundCallUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          to: contactForCall.phone,
          from: fromNum,
          stack: 'kokoro-vad',
          ...(placedByUserId ? { placedByUserId } : {}),
          scriptData: {
            _id: selectedNumberDoc.outboundScriptId,
            firstName: contactForCall.firstName || null,
            lastName: contactForCall.lastName || null,
          },
          CID: callRecord.id,
          expectedDob: contactForCall.dob || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        alert(`Call started. SID: ${data.callSid || '—'}`);
        setCallRecord(null);
      } else {
        alert(data?.message || 'Failed to start call.');
      }
    } catch (e) {
      alert(e.message || 'Error starting call.');
    } finally {
      setCallStarting(false);
    }
  }

  const fieldNames = group?.fieldNames || [];

  return (
    <div className="flex flex-col min-h-0 bg-slate-50 text-slate-900">
      {/* Bar: Back + group name */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100"
          aria-label="Back to groups"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-slate-900 truncate">{group?.groupName || 'Group'}</h2>
          <p className="text-sm text-slate-500">{total} record(s)</p>
        </div>
      </div>

      {/* Filters */}
      <section className="w-full px-4 py-4 min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search records…"
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-sm"
              aria-label="Search"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(''); setPage(1); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100"
                aria-label="Clear"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setQ(''); setPage(1); }}
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm hover:bg-slate-100"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-3 py-2.5 text-sm hover:bg-slate-800"
          >
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} /> Refresh
          </button>
        </div>
      </section>

      {/* Table */}
      <main className="flex-1 min-h-0 w-full px-4 pb-8 overflow-auto">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {fieldNames.map((name) => (
                    <th key={name} className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                      {name}
                    </th>
                  ))}
                  <th className="text-left font-semibold px-4 py-3">Created</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading && (
                  [...Array(6)].map((_, i) => (
                    <tr key={`sk-${i}`} className="animate-pulse">
                      {fieldNames.map((name) => (
                        <td key={name} className="px-4 py-4">
                          <div className="h-4 w-24 bg-slate-200 rounded" />
                        </td>
                      ))}
                      <td className="px-4 py-4"><div className="h-4 w-28 bg-slate-200 rounded" /></td>
                      <td className="px-4 py-4"><div className="h-8 w-24 bg-slate-200 rounded ml-auto" /></td>
                    </tr>
                  ))
                )}
                {!loading && records.length === 0 && (
                  <tr>
                    <td colSpan={fieldNames.length + 2} className="px-4 py-16 text-center text-slate-500">
                      <Info className="w-6 h-6 mx-auto mb-2 opacity-60" />
                      <div className="font-medium">No records found</div>
                      <div className="text-sm">Try adjusting your search.</div>
                    </td>
                  </tr>
                )}
                {!loading && records.map((rec) => (
                  <tr key={rec.id} className="hover:bg-slate-50">
                    {fieldNames.map((name) => (
                      <td key={name} className="px-4 py-4 text-slate-700 max-w-[16rem]">
                        {truncate(rec.fields?.[name] ?? '—', 60)}
                      </td>
                    ))}
                    <td className="px-4 py-4 text-slate-600 whitespace-nowrap">
                      {formatDate(rec.createdAt)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => openCallModal(rec)}
                          disabled={!phoneDigits(getContactForCall(fieldNames, rec.fields).phone)}
                          title={phoneDigits(getContactForCall(fieldNames, rec.fields).phone) ? 'Call this contact' : 'No phone number'}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 text-white px-3 py-2 text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Phone className="w-4 h-4" /> Call
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelected(rec)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-100"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditRecord(rec); setEditDraft({ ...(rec.fields || {}) }); }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-100"
                        >
                          <Edit3 className="w-4 h-4" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteRecord(rec)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-white">
            <div className="text-sm text-slate-600 shrink-0">
              Showing <span className="font-medium">{records.length}</span> of <span className="font-medium">{total}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-sm"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-slate-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <span className="text-sm">Page <span className="font-semibold">{page}</span></span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-slate-50"
                  onClick={() => setPage((p) => (hasMore ? p + 1 : p))}
                  disabled={!hasMore}
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm">
            {error}
          </div>
        )}
      </main>

      {/* Detail drawer */}
      <div className={clsx('fixed inset-0 z-40 transition', selected ? 'pointer-events-auto' : 'pointer-events-none')}>
        <div
          className={clsx('absolute inset-0 bg-black/30 transition-opacity', selected ? 'opacity-100' : 'opacity-0')}
          onClick={() => setSelected(null)}
          aria-hidden
        />
        <aside
          className={clsx(
            'absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl border-l border-slate-200 p-6 overflow-y-auto transition-transform',
            selected ? 'translate-x-0' : 'translate-x-full'
          )}
          aria-label="Record details"
        >
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-lg font-semibold text-slate-900 truncate">Record details</h3>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="rounded-lg p-2 hover:bg-slate-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-6 space-y-4">
            {fieldNames.map((name) => (
              <div key={name} className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs font-semibold text-slate-500 mb-1">{name}</div>
                <p className="text-sm text-slate-900 whitespace-pre-wrap">{selected?.fields?.[name] ?? '—'}</p>
              </div>
            ))}
            <div className="rounded-2xl border border-slate-200 p-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Created</div>
                <div className="text-sm">{formatDate(selected?.createdAt)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Updated</div>
                <div className="text-sm">{formatDate(selected?.updatedAt)}</div>
              </div>
            </div>
            <div className="flex gap-2 pt-2 flex-wrap">
              <button
                type="button"
                onClick={() => openCallModal(selected)}
                disabled={!phoneDigits(getContactForCall(fieldNames, selected?.fields).phone)}
                title={phoneDigits(getContactForCall(fieldNames, selected?.fields).phone) ? 'Call this contact' : 'No phone number'}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 text-white px-3 py-2 text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Phone className="w-4 h-4" /> Call
              </button>
              <button
                type="button"
                onClick={() => { setEditRecord(selected); setEditDraft({ ...(selected?.fields || {}) }); setSelected(null); }}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800"
              >
                <Edit3 className="w-4 h-4" /> Edit
              </button>
              <button
                type="button"
                onClick={() => { setDeleteRecord(selected); setSelected(null); }}
                className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Call modal */}
      {callRecord && contactForCall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">Call from CRM</h3>
              <button
                type="button"
                onClick={() => setCallRecord(null)}
                className="rounded-lg p-2 hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="rounded-xl bg-slate-50 p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact</p>
                <p className="font-medium text-slate-900">
                  {contactForCall.firstName || contactForCall.lastName
                    ? [contactForCall.firstName, contactForCall.lastName].filter(Boolean).join(' ')
                    : '—'}
                </p>
                {contactForCall.dob && (
                  <p className="text-sm text-slate-600">DOB: {contactForCall.dob}</p>
                )}
                <p className="text-sm text-slate-700">Phone: {contactForCall.phone || '—'}</p>
              </div>
              {!contactForCall.phone ? (
                <p className="text-sm text-amber-700">This record has no phone number. Add a Phone field value to call.</p>
              ) : (
                <>
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700 mb-1">From number</span>
                    <select
                      value={callFromNumber}
                      onChange={(e) => setCallFromNumber(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm bg-white"
                    >
                      {numbersWithOutbound.length === 0 ? (
                        <option value="">No numbers with outbound script</option>
                      ) : (
                        numbersWithOutbound.map((n) => (
                          <option key={n.number || n._id} value={n.number}>
                            {n.display || n.displayNumber || n.number}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <p className="text-xs text-slate-500">
                    First name, last name, and DOB are sent to the call so the agent can use them.
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setCallRecord(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={startCall}
                disabled={callStarting || !contactForCall.phone || !selectedNumberDoc?.outboundScriptId}
                className="inline-flex items-center gap-2 rounded-xl bg-teal-600 text-white px-4 py-2 text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Phone className="w-4 h-4" />
                {callStarting ? 'Starting…' : 'Start call'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">Edit record</h3>
              <button type="button" onClick={() => { setEditRecord(null); setEditDraft({}); }} className="rounded-lg p-2 hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 space-y-4">
              {fieldNames.map((name) => (
                <label key={name} className="block">
                  <span className="block text-sm font-medium text-slate-700 mb-1">{name}</span>
                  <textarea
                    rows={2}
                    value={editDraft[name] ?? ''}
                    onChange={(e) => setEditDraft((d) => ({ ...d, [name]: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => { setEditRecord(null); setEditDraft({}); }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-60"
              >
                <Edit3 className="w-4 h-4" />
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl p-6">
            <h3 className="text-lg font-semibold text-slate-900">Delete record?</h3>
            <p className="mt-2 text-sm text-slate-600">This cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteRecord(null)}
                disabled={deleteDeleting}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteDeleting}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 text-white px-4 py-2 text-sm hover:bg-rose-700 disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" />
                {deleteDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
